import React, { useState, useEffect, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import {
  X,
  Users,
  Check,
  Mail,
  Filter,
  Plus,
  Trash2,
} from 'lucide-react'
import { useUserOptions, type UserOption } from '@/shared/hooks/useUserOptions'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { type SpaceDetail, saveSpaceMembers, addSpaceMember, removeSpaceMember, createUser, type CreateUserInput } from '../api'

interface SpaceMembersModalProps {
  open: boolean
  onClose: () => void
  space: SpaceDetail
  onMembersChange?: (assignedUsers: UserOption[]) => void
}

export function SpaceMembersModal({
  open,
  onClose,
  space,
  onMembersChange,
}: SpaceMembersModalProps) {
  // Unfiltered — every active user, so every department tab has something to
  // show, regardless of which department this project is "relevant" to.
  const { data: allUsers = [] } = useUserOptions(null)

  // The departments this project belongs to (master data; set when it was created).
  const relevantDepartments = useMemo(() => space.departments ?? [], [space.departments])

  // Every department that has people, from the users themselves (so a department
  // added in master data shows up here automatically).
  const allDepartments = useMemo(
    () => Array.from(new Set(allUsers.map((u) => u.department).filter((d): d is string => !!d))).sort((a, b) => a.localeCompare(b)),
    [allUsers]
  )

  // A project with no departments has nothing to narrow to, so start on everyone.
  const [activeFilter, setActiveFilter] = useState<'relevant' | 'all'>(relevantDepartments.length > 0 ? 'relevant' : 'all')

  const validIds = useMemo(() => new Set(allUsers.map((u) => u.id)), [allUsers])

  // The space's saved members (backend is the source of truth).
  const resolveInitialAssignedIds = (): string[] =>
    (space.members ?? []).map((m) => m.userId).filter((id) => id && validIds.has(id))

  // State of assigned user ids for this space
  const [assignedIds, setAssignedIds] = useState<string[]>([])

  // Invite user form state
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteDepartment, setInviteDepartment] = useState('')
  const [invitePassword, setInvitePassword] = useState('')
  const [inviteRole, setInviteRole] = useState<'MEMBER' | 'ADMIN'>('MEMBER')
  const [inviteSaving, setInviteSaving] = useState(false)

  // Remove confirmation state
  const [removingId, setRemovingId] = useState<string | null>(null)

  // Keep state in sync whenever space, real users, or relevant departments change
  useEffect(() => {
    if (allUsers.length === 0) return
    setAssignedIds(resolveInitialAssignedIds())
  }, [
    space.id,
    JSON.stringify((space.members ?? []).map((m) => m.userId)),
    JSON.stringify(relevantDepartments),
    allUsers.length,
  ])

  const notifyChange = async (updated: string[], previous: string[]) => {
    // Persist to backend; roll back the optimistic update if it doesn't save.
    try {
      await saveSpaceMembers(space.id, updated)
    } catch (err) {
      setAssignedIds(previous)
      toast.error(getApiErrorMessage(err, 'update space members'))
      return
    }

    const assignedList = allUsers.filter((u) => updated.includes(u.id))
    onMembersChange?.(assignedList)

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('spaceMembersUpdated', {
          detail: {
            spaceId: space.id,
            assignedIds: updated,
            assignedUsers: assignedList,
            count: updated.length,
          },
        })
      )
    }
  }

  const toggleAssignment = (userId: string) => {
    const previous = assignedIds
    const updated = previous.includes(userId) ? previous.filter((id) => id !== userId) : [...previous, userId]
    setAssignedIds(updated)
    notifyChange(updated, previous)
  }

  const selectAllInDepartment = (department: string) => {
    const previous = assignedIds
    const deptUserIds = allUsers.filter((u) => u.department === department).map((u) => u.id)
    const allSelected = deptUserIds.every((id) => previous.includes(id))
    const updated = allSelected
      ? previous.filter((id) => !deptUserIds.includes(id))
      : Array.from(new Set([...previous, ...deptUserIds]))
    setAssignedIds(updated)
    notifyChange(updated, previous)
  }

  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || !inviteName.trim() || !inviteDepartment.trim()) {
      toast.error('Name, email, and department are required')
      return
    }

    setInviteSaving(true)
    const previous = assignedIds
    try {
      const payload: CreateUserInput = {
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        department: inviteDepartment.trim(),
        username: inviteName.trim().split(' ')[0].toLowerCase(),
        departmentRole: 'MEMBER',
        role: inviteRole === 'ADMIN' ? 'ADMIN' : 'MEMBER',
        password: invitePassword || undefined,
      }

      const newUser = await createUser(payload)
      const newUserId = newUser?.id ?? newUser?.userId

      if (!newUserId) {
        throw new Error('User created but no identifier returned')
      }

      const updated = Array.from(new Set([...assignedIds, newUserId]))
      setAssignedIds(updated)
      await notifyChange(updated, previous)

      setInviteOpen(false)
      setInviteEmail('')
      setInviteName('')
      setInviteDepartment('')
      setInvitePassword('')
      setInviteRole('MEMBER')
      toast.success('User created and added to space')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'invite user'))
    } finally {
      setInviteSaving(false)
    }
  }

  const handleRemoveMember = async (userId: string) => {
    const previous = assignedIds
    const updated = assignedIds.filter((id) => id !== userId)
    setRemovingId(null)
    try {
      await removeSpaceMember(space.id, userId)
      setAssignedIds(updated)
      toast.success('Removed from space')
    } catch (err) {
      setAssignedIds(previous)
      toast.error(getApiErrorMessage(err, 'remove member'))
    }
  }

  const departmentsForInvite = useMemo(
    () => allDepartments,
    [allDepartments]
  )

  // Which departments to list: the project's own, or everyone's. When showing the
  // project's departments, also list any other department that has an assigned
  // person, so the count in the header always matches what is on screen.
  const displayedDepartments = useMemo(() => {
    let names = activeFilter === 'relevant' ? relevantDepartments : allDepartments

    if (activeFilter === 'relevant') {
      const others = allDepartments.filter(
        (name) => !relevantDepartments.includes(name) && allUsers.some((u) => u.department === name && assignedIds.includes(u.id))
      )
      names = [...relevantDepartments, ...others]
    }

    return names
      .map((name) => ({ name, users: allUsers.filter((u) => u.department === name) }))
      .filter((d) => d.users.length > 0)
  }, [activeFilter, relevantDepartments, allDepartments, assignedIds, allUsers])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="flex flex-col w-full max-w-2xl max-h-[90vh] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-foreground">
                  Space Members ({assignedIds.length} Assigned)
                </h2>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                  {assignedIds.length} active on this project
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Assign employees from respective departments to work on{' '}
                <span className="font-semibold text-foreground">{space.name}</span> [{space.key}]
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter bar: Project Relevant vs All Directory */}
        <div className="flex flex-col gap-2 px-6 py-2.5 bg-muted/10 border-b border-border/60 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-muted-foreground mr-1" />
              <span className="text-[11px] font-medium text-muted-foreground">Showing:</span>
              <div className="inline-flex rounded-lg bg-muted/60 p-0.5 border border-border/40">
                <button
                  type="button"
                  onClick={() => setActiveFilter('relevant')}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                    activeFilter === 'relevant'
                      ? 'bg-background text-foreground shadow-2xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Project Departments ({relevantDepartments.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveFilter('all')}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                    activeFilter === 'all'
                      ? 'bg-background text-foreground shadow-2xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All Company Departments
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setInviteOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Invite user</span>
              </button>
            </div>
          </div>

          <span className="text-[11px] text-muted-foreground">
            Check the box to assign / unassign
          </span>

          {inviteOpen && (
            <div className="rounded-xl border border-border bg-background p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-muted-foreground mb-1">Full name</label>
                  <input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-muted-foreground mb-1">Email</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="jane@company.com"
                    className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-muted-foreground mb-1">Department</label>
                  <select
                    value={inviteDepartment}
                    onChange={(e) => setInviteDepartment(e.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="">Select department</option>
                    {departmentsForInvite.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-muted-foreground mb-1">Password</label>
                  <input
                    type="text"
                    value={invitePassword}
                    onChange={(e) => setInvitePassword(e.target.value)}
                    placeholder="Optional"
                    className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="inline-flex rounded-lg bg-muted/60 p-0.5 border border-border/40">
                  <button
                    type="button"
                    onClick={() => setInviteRole('MEMBER')}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                      inviteRole === 'MEMBER' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Member
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteRole('ADMIN')}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                      inviteRole === 'ADMIN' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Admin
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setInviteOpen(false)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-border bg-background hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={inviteSaving}
                    onClick={handleInviteUser}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {inviteSaving ? 'Saving...' : 'Add & Assign'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Members List Grouped by Department */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {displayedDepartments.map(({ name, users }) => {
            const allDeptSelected = users.every((u) => assignedIds.includes(u.id))

            return (
              <div key={name} className="space-y-3">
                {/* Department Section Header */}
                <div className="flex items-center justify-between pb-1.5 border-b border-border/60">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">
                      {name}
                    </h3>
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      ({users.length} members)
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => selectAllInDepartment(name)}
                    className="text-[11px] text-primary hover:underline font-medium cursor-pointer"
                  >
                    {allDeptSelected ? 'Unselect all' : 'Select all'}
                  </button>
                </div>

                {/* User Cards List */}
                <div className="grid grid-cols-1 gap-2.5">
                  {users.map((u) => {
                    const isAssigned = assignedIds.includes(u.id)

                    return (
                      <div
                        key={u.id}
                        onClick={() => toggleAssignment(u.id)}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                          isAssigned
                            ? 'bg-primary/5 border-primary/40 shadow-2xs ring-1 ring-primary/20'
                            : 'bg-card hover:bg-muted/40 border-border/70 hover:border-border'
                        }`}
                      >
                        {/* Left: Checkbox + Avatar + Info */}
                        <div className="flex items-center gap-3.5">
                          {/* Checkbox button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleAssignment(u.id)
                            }}
                            className={`flex h-5 w-5 items-center justify-center rounded-md border transition-all cursor-pointer ${
                              isAssigned
                                ? 'bg-primary border-primary text-primary-foreground shadow-2xs'
                                : 'border-input bg-background hover:border-primary text-transparent'
                            }`}
                            title={isAssigned ? 'Unassign from project' : 'Assign to project'}
                          >
                            <Check className="h-3.5 w-3.5 stroke-[3]" />
                          </button>

                          {/* Avatar Circle with Initials */}
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white font-bold text-xs shadow-xs"
                            style={{ backgroundColor: u.color }}
                          >
                            {u.initials}
                          </div>

                          {/* Name, Handle & Title */}
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-foreground">
                                {u.name}
                              </span>
                              {u.username && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  @{u.username}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{u.title}</span>
                              {u.email && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1 font-mono text-[11px]">
                                    <Mail className="h-3 w-3 opacity-60" />
                                    {u.email}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: Badges (Admin/Employee, Active Status) */}
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Role Badge */}
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                              u.role === 'ADMIN' || u.role === 'SUPERADMIN'
                                ? 'bg-indigo-500/10 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800'
                                : 'bg-muted text-muted-foreground border-border'
                            }`}
                          >
                            {u.role === 'SUPERADMIN' ? 'Super Admin' : u.role === 'ADMIN' ? 'Admin' : 'Employee'}
                          </span>

                          {/* Active Status Badge with Pulsing Green Dot */}
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Active
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3.5 border-t border-border bg-muted/20">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{assignedIds.length}</span> member(s) selected
            to manage and deliver this project.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm cursor-pointer transition-all"
            >
              Done & Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
export default SpaceMembersModal
